import type { MetaFunction } from "@remix-run/node";
import title from "~/kernel/meta/title";
import NavigationBar from "~/ui/NavigationBar";
import Footer from "~/ui/Footer";
import findPopularCompanies from "~/routes/_index/findPopularCompanies";
import { bannerUrl } from "~/company/helpers/bannerUrl";
import Hero from "~/routes/_index/Hero";
import PopularCompanies from "~/routes/_index/PopularCompanies";
import translationHandle from "~/kernel/internationalization/translationHandle";
import WaitForTranslations from "~/kernel/internationalization/WaitForTranslations";
import useTranslation from "~/kernel/internationalization/useTranslation";
import { Link } from "@remix-run/react";
import ROUTES from "~/kernel/routing/ROUTES";
import { Icon, Text, Heading } from "~/ui";
import type { LoaderData } from "./LoaderData";
import StartPageContainer from "./StartPageContainer";
import createLoader from "~/kernel/server/createLoader";
import type { ReactNode } from "react";

export const handle = translationHandle({
  ns: { root: true },
});

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: title(data.metaTitle) },
    { description: data.metaDescription },
  ];
};

export const loader = createLoader(
  async ({ translation, json }) => {
    const popularCompanies = await findPopularCompanies();
    return json<LoaderData>({
      metaTitle: translation.tt("metaTitle"),
      metaDescription: translation.tt("metaDescription"),
      popularCompanies: popularCompanies.map((c) => ({
        ...c,
        bannerUrl: bannerUrl(c),
      })),
    });
  },
  { handle, translationScope: "index" },
);

export default function Index() {
  const { tt } = useTranslation("index");
  return (
    <div>
      <WaitForTranslations>
        <NavigationBar />
        <div className="flex min-h-screen flex-col gap-20 pb-30">
          <Hero />
          <div className="flex-1">
            <StartPageContainer>
              <div className="space-y-16">
                <div className="space-y-12">
                  <div className="space-y-3 text-center">
                    <div className="text-slate-800 md:text-3xl text-2xl font-bold">
                      {tt("popularCompaniesTitle")}
                    </div>
                    <RoundedLinkButton to={ROUTES.companies.path()}>
                      {tt("showAll")} <Icon name="arrow-right" />
                    </RoundedLinkButton>
                  </div>
                  <PopularCompanies />
                </div>

                <div className="space-y-3 max-w-md text-center mx-auto">
                  <Heading>{tt("groomerCta.title")}</Heading>
                  <div className="space-y-5">
                    <Text>{tt("groomerCta.description")}</Text>
                    <RoundedLinkButton to={ROUTES.connectCompany.path({})}>
                      {tt("groomerCta.link")} <Icon name="arrow-right" />
                    </RoundedLinkButton>
                  </div>
                </div>
              </div>
            </StartPageContainer>
          </div>
          <Footer />
        </div>
      </WaitForTranslations>
    </div>
  );
}

type RoundedLinkButtonProps = {
  to: string;
  children: ReactNode;
};

function RoundedLinkButton({ to, children }: RoundedLinkButtonProps) {
  return (
    <Link
      to={to}
      className="text-white inline-block bg-primary px-5 py-2 rounded-full cursor-pointer text-sm"
    >
      {children}
    </Link>
  );
}
